import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'

import {
  getBrandList,
  getHubList,
  getProductTypeList,
  getPICList,
  getBusinessLineList,
  getHardcodeReasonList,
  getSourceList,
  getParetoList,
  getCategoryL1List,
  getCategoryL2List,
  getCampaignTypeList,
  getVendorList,
  getPaydayPeriodList,
  getCategoryDemandList,
  getCampaignStatusList,
  getFCStatusList,
  getProductSuggestions,
} from '../../services/forecastFilter'
import type { FilterOptionType } from '../../@types/forecastFilterTypes'

const sliceName = 'forecastFilter'

export const fetchProductSuggestions = createAsyncThunk(
  `${sliceName}/fetchProductSuggestions`,
  async (
    { keys, values, cursor = '' }: { keys: string; values: string; cursor?: string },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await getProductSuggestions(keys, values, cursor)
      return {
        suggestions: data?.data ?? [],
        nextCursor: data?.pagination?.next_cursor ?? '',
        append: Boolean(cursor),
      }
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchBrandOptions = createAsyncThunk(
  `${sliceName}/fetchBrandOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getBrandList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.value,
          value: item.key,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchHubOptions = createAsyncThunk(
  `${sliceName}/fetchHubOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = (await getHubList(name)) as {
        data: { content: { location_id: number; location_name: string }[] }
      }
      return (data?.content ?? []).map(
        (item): FilterOptionType => ({
          label: item.location_name,
          value: item.location_id,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchProductTypeOptions = createAsyncThunk(
  `${sliceName}/fetchProductTypeOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getProductTypeList()
      return (data?.data ?? []).map((item) => ({
        label: item.value,
        value: item.value,
      }))
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchCategoryL1Options = createAsyncThunk(
  `${sliceName}/fetchCategoryL1Options`,
  async (keyword: string, { rejectWithValue }) => {
    try {
      const { data } = await getCategoryL1List(keyword)
      const content = data?.data ?? []

      const l1: FilterOptionType[] = content.map((item) => ({
        label: `${item.key} - ${item.value}`,
        value: item.key,
      }))

      return l1
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchCategoryL2Options = createAsyncThunk(
  `${sliceName}/fetchCategoryL2Options`,
  async (keyword: string, { rejectWithValue }) => {
    try {
      const { data } = await getCategoryL2List(keyword)
      const content = data?.data ?? []

      const l2: FilterOptionType[] = content.map((item) => ({
        label: `${item.key} - ${item.value}`,
        value: item.key,
      }))

      return l2
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchPICOptions = createAsyncThunk(
  `${sliceName}/fetchPICOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getPICList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.value,
          value: item.value,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchBusinessLineOptions = createAsyncThunk(
  `${sliceName}/fetchBusinessLineOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = (await getBusinessLineList()) as {
        data: { data: { id: number; value: string }[] }
      }
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.value,
          value: item.value,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchHardcodeReasonOptions = createAsyncThunk(
  `${sliceName}/fetchHardcodeReasonOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getHardcodeReasonList()
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.value,
          value: item.value,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchSourceOptions = createAsyncThunk(
  `${sliceName}/fetchSourceOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getSourceList()
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.value,
          value: item.value,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchParetoOptions = createAsyncThunk(
  `${sliceName}/fetchParetoOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getParetoList()

      return (data?.data ?? []).map(
        (item): FilterOptionType => ({
          label: item.name,
          value: item.name,
        }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchCampaignTypeOptions = createAsyncThunk(
  `${sliceName}/fetchCampaignTypeOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getCampaignTypeList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.value }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchVendorOptions = createAsyncThunk(
  `${sliceName}/fetchVendorOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getVendorList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.key }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchPaydayPeriodOptions = createAsyncThunk(
  `${sliceName}/fetchPaydayPeriodOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getPaydayPeriodList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.value }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchCategoryDemandOptions = createAsyncThunk(
  `${sliceName}/fetchCategoryDemandOptions`,
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await getCategoryDemandList(name)
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.value }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchCampaignStatusOptions = createAsyncThunk(
  `${sliceName}/fetchCampaignStatusOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getCampaignStatusList()
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.value }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchFCStatusOptions = createAsyncThunk(
  `${sliceName}/fetchFCStatusOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getFCStatusList()
      return (data?.data ?? []).map(
        (item): FilterOptionType => ({ label: item.value, value: item.value }),
      )
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)
